import { Landmark, Mail, MoreHorizontal, Send } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  useToast,
} from "randmarcomps";
import {
  useDeleteV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberResellerResellerIdMutation,
  usePostV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberResellerResellerIdEmailMutation,
} from "randmar-api-client";
import { useManufacturer } from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import { RootStoreContext } from "../../stores/rootStore";
import Reseller from "../../stores/reseller/Reseller";
import InlineSpinner from "../util/InlineSpinner";
import ResellerActions from "../reseller/ResellerActions";
import ButtonWithDialogConfirm from "../util/ButtonWithDialogConfirm";

interface BidResellersGridProps {
  resellers?: Reseller[];
  bidNumber: string;
  onChanged?: () => void;
}

const BidResellersGrid: React.FC<BidResellersGridProps> = ({ bidNumber, resellers, onChanged }) => {
  const { applicationId } = useManufacturer();
  const { userStore } = React.useContext(RootStoreContext);
  const { toast } = useToast();

  const [removingResellerId, setRemovingResellerId] = React.useState<string | null>(null);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = React.useState(false);
  const [selectedReseller, setSelectedReseller] = React.useState<Reseller | null>(null);
  const [email, setEmail] = React.useState(userStore.user?.profile?.email ?? "");
  const [isSendingEmail, setIsSendingEmail] = React.useState(false);

  const [sendEmailMutation] = usePostV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberResellerResellerIdEmailMutation();
  const [removeResellerMutation] = useDeleteV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberResellerResellerIdMutation();

  const isSuperAdmin = userStore.isSuperAdmin();

  const handleRemoveReseller = React.useCallback(
    async (resellerId: string) => {
      setRemovingResellerId(resellerId);
      try {
        await removeResellerMutation({
          routeManufacturerId: applicationId,
          bidNumber,
          resellerId,
        }).unwrap();
        toast({ variant: "success", title: "Success", description: `${resellerId} removed from ${bidNumber}` });
        onChanged?.();
      } catch (error) {
        console.error("Failed to remove reseller from opportunity:", error);
        toast({ variant: "destructive", title: "Error", description: `${resellerId} could not be removed from ${bidNumber}` });
      } finally {
        setRemovingResellerId(null);
      }
    },
    [applicationId, bidNumber, onChanged, removeResellerMutation, toast]
  );

  const handleOpenEmailDialog = React.useCallback(
    (reseller: Reseller) => {
      setSelectedReseller(reseller);
      setEmail(userStore.user?.profile?.email ?? "");
      setIsEmailDialogOpen(true);
    },
    [userStore.user?.profile?.email]
  );

  const sendResellerBidPricingEmail = React.useCallback(async () => {
    if (!selectedReseller?.resellerId) return;
    if (!email) return;

    setIsSendingEmail(true);
    try {
      await sendEmailMutation({
        routeManufacturerId: applicationId,
        bidNumber,
        resellerId: selectedReseller.resellerId,
        emailAddress: email,
      }).unwrap();
      toast({ variant: "success", title: "Success", description: `Bid pricing sent to ${email}` });
      setIsEmailDialogOpen(false);
    } catch (error) {
      console.error("Failed to send bid pricing email:", error);
      toast({ variant: "destructive", title: "Error", description: "Failed to send bid pricing." });
    } finally {
      setIsSendingEmail(false);
    }
  }, [applicationId, bidNumber, email, selectedReseller?.resellerId, sendEmailMutation, toast]);

  const hasResellers = (resellers?.length ?? 0) > 0;

  if (!hasResellers) {
    return (
      <Card className="w-full rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
        <p className="text-[15px] text-slate-600">No resellers are linked to this opportunity.</p>
      </Card>
    );
  }

  return (
    <Card className="w-full overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
      <div className="w-full overflow-x-auto">
        <Table>
          <TableHeader className="bg-gray-50">
            <TableRow className="h-12">
              <TableHead className="w-14 text-center text-[12px] font-semibold uppercase tracking-wide text-gray-500">
                Hold
              </TableHead>
              <TableHead className="w-14 text-center text-[12px] font-semibold uppercase tracking-wide text-gray-500">
                Logo
              </TableHead>
              <TableHead className="min-w-[220px] text-[12px] font-semibold uppercase tracking-wide text-gray-500">
                Reseller
              </TableHead>
              <TableHead className="min-w-[200px] text-[12px] font-semibold uppercase tracking-wide text-gray-500">
                Location
              </TableHead>
              <TableHead className="min-w-[160px] text-[12px] font-semibold uppercase tracking-wide text-gray-500">
                Phone
              </TableHead>
              <TableHead className="w-44 text-center text-[12px] font-semibold uppercase tracking-wide text-gray-500">
                Actions
              </TableHead>
              {isSuperAdmin ? (
                <TableHead className="w-16 text-center text-[12px] font-semibold uppercase tracking-wide text-gray-500">
                  Remove
                </TableHead>
              ) : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {(resellers || []).map((row) => {
              const isOnHold = Boolean(row.onHold);
              const resellerId = row.resellerId || "";

              return (
                <TableRow key={resellerId} className="h-14 transition-colors hover:bg-gray-50">
                  <TableCell className="text-center">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-gray-100 bg-white">
                            <Landmark className={isOnHold ? "h-4 w-4 text-rose-600" : "h-4 w-4 text-emerald-600"} aria-hidden="true" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{isOnHold ? "On hold" : "Not on hold"}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>

                  <TableCell className="text-center">
                    <Link to={Path.reseller(applicationId, resellerId)} aria-label={`View reseller ${row.name ?? resellerId}`}>
                      <Avatar className="h-9 w-9 border border-gray-200">
                        <AvatarImage
                          alt={row.name ?? ""}
                          src={`https://api.randmar.io/v4/Partner/${resellerId}/Account/Logo`}
                        />
                        <AvatarFallback className="text-xs font-semibold text-gray-700">
                          {(row.name || resellerId).slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    </Link>
                  </TableCell>

                  <TableCell className="text-[15px]">
                    <Link to={Path.reseller(applicationId, resellerId)} className="font-medium text-gray-800 hover:underline">
                      {row.name || resellerId}
                    </Link>
                  </TableCell>

                  <TableCell className="text-[15px] text-gray-700">
                    {[row.city, row.province, row.country].filter(Boolean).join(", ") || "—"}
                  </TableCell>

                  <TableCell className="text-[15px] text-gray-700">{row.phone || "—"}</TableCell>

                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-2">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              size="icon"
                              variant="outline"
                              className="h-10 w-10 rounded-xl"
                              onClick={() => handleOpenEmailDialog(row)}
                              aria-label={`Send bid pricing email for ${row.name ?? resellerId}`}
                            >
                              <Mail className="h-4 w-4" aria-hidden="true" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Send bid pricing</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>

                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            size="icon"
                            variant="outline"
                            className="h-10 w-10 rounded-xl"
                            aria-label="Open reseller actions"
                          >
                            <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent align="end" className="w-[300px] rounded-2xl border border-gray-100 p-0 shadow-lg">
                          <ResellerActions reseller={row} />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </TableCell>

                  {isSuperAdmin ? (
                    <TableCell className="text-center">
                      {removingResellerId === resellerId ? (
                        <InlineSpinner size="md" tone="muted" decorative className="mx-auto" />
                      ) : (
                        <ButtonWithDialogConfirm
                          fab
                          buttonTitle={`Remove ${row.name ?? resellerId} from ${bidNumber}`}
                          text={`Remove ${row.name ?? resellerId} from opportunity ${bidNumber}?`}
                          onConfirmation={() => handleRemoveReseller(resellerId)}
                        />
                      )}
                    </TableCell>
                  ) : null}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={isEmailDialogOpen} onOpenChange={setIsEmailDialogOpen}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Send bid pricing</DialogTitle>
            <DialogDescription>
              Send opportunity pricing to a reseller email address.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="opportunity-email">Email</Label>
              <Input
                id="opportunity-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="recipient@example.com"
                disabled={isSendingEmail}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              onClick={sendResellerBidPricingEmail}
              disabled={isSendingEmail || !email}
              className="rounded-xl"
            >
              {isSendingEmail ? (
                <>
                  <InlineSpinner className="mr-2" decorative />
                  Sending…
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" aria-hidden="true" />
                  Send
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default BidResellersGrid;
